import { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { CustomPropertyFormFields } from '../pages/CustomProperty/CustomPropertyForm.types';
import { HotelFormFields } from '../pages/Hotel/HotelForm.types';
import {
  updateHotelFormDataFromGooglePlace,
  updateHotelFormDataFromPreDefinedPlace,
  resetHotelFormFields,
  updateHotelFormDataFromCatalogProperty,
} from '../utils/Accommodations.utils';
import { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { usePreDefinedGooglePlaces } from '@shared/utils/hooks/usePreDefinedGooglePlaces';
import { addError } from '@shared/utils/logger';
import { AccommodationPlaceSourceType, FindMatchingAccommodationCatalogPropertyQuery, HotelTypeEnum } from '@graphql/generated';
import { HotelInventory } from '@apps/admin/routes/Planning/hooks/HotelListSearcher/useZentrumHubHotelListSearcher';
import { useFindMatchingAccommodationCatalogProperty } from './useFindMatchingAccommodationCatalogProperty';

const transformPropertyToInventory = (findMatchingAccommodationCatalogProperty: FindMatchingAccommodationCatalogPropertyQuery['findMatchingAccommodationCatalogProperty']) => {
  const accommodationCatalogProperty = findMatchingAccommodationCatalogProperty?.accommodationCatalogProperty;
  if (!accommodationCatalogProperty) return null;
  const hotelInventory: HotelInventory = {
    address: accommodationCatalogProperty.address?.street as string,
    city: accommodationCatalogProperty.address?.city as string,
    country: accommodationCatalogProperty.address?.country as string,
    description: accommodationCatalogProperty.plainTextLongDescription,
    displayName: accommodationCatalogProperty.name,
    location: {
      lat: accommodationCatalogProperty.location.latitude as number,
      lon: accommodationCatalogProperty.location.longitude as number,
    },
    photo: accommodationCatalogProperty.photos[0].url,
    photos: accommodationCatalogProperty.photos.map(photo => photo.url),
    postalCode: accommodationCatalogProperty.address?.postalCode as string,
    ratingCount: accommodationCatalogProperty.userRating?.ratingCount as number,
    starRating: accommodationCatalogProperty.starRating as number,
    state: accommodationCatalogProperty.address?.state as string,
    userRating: accommodationCatalogProperty.userRating?.ratingValue as number,
    joyPlaceId: accommodationCatalogProperty.joyPlaceId,

    // New API does not support these values - Added default
    zhId: '',
    distance: 0,
    isPreferredHotel: false,
    hotelUrl: '',
    hotelType: HotelTypeEnum.Hotel,
  };
  return hotelInventory;
};

interface HandleOnSelectHotelProps {
  isHotelForm?: boolean;
  setShowMessage: (show: boolean, zh?: string, hotelInventory?: HotelInventory) => void;
  setHotelPhoto?: (photo: string) => void;
  setAutoGeneratedPhoto?: (photo: string) => void;
  updateFormAfterFetchHotelDetails: (updateFormValues: CustomPropertyFormFields | HotelFormFields) => void;
  isUpdateCustomUrlLocally?: boolean;
  isUpdateCustomName?: boolean;
}

export const useHandleOnSelectHotel = (props: HandleOnSelectHotelProps) => {
  const { isHotelForm, setShowMessage, setHotelPhoto, setAutoGeneratedPhoto, updateFormAfterFetchHotelDetails, isUpdateCustomUrlLocally, isUpdateCustomName = false } = props;

  const { preDefinedPlacesEnabled, getPreDefinedPlaceById } = usePreDefinedGooglePlaces();
  const { findMatchingAccommodationCatalogProperty } = useFindMatchingAccommodationCatalogProperty();

  const handleOnSelectHotel = (updateFormValues: CustomPropertyFormFields | HotelFormFields, location: Maybe<LocationInputV2Response | Location>) => {
    // Reset form values on change location
    resetHotelFormFields(updateFormValues, isHotelForm);
    setShowMessage(false);
    setHotelPhoto?.('');
    if (!location?.placeId) {
      addError('Place id does not exists', { context: 'accommodationsEditor', action: 'accommodationsPlaceSelected', location });
      updateFormAfterFetchHotelDetails(updateFormValues);
      return;
    }
    // Set from values from google location info
    updateHotelFormDataFromGooglePlace(updateFormValues, location, isHotelForm, isUpdateCustomName);
    if (preDefinedPlacesEnabled && location.placeId) {
      const preDefinedPlace = getPreDefinedPlaceById(location.placeId);
      if (preDefinedPlace && preDefinedPlace.shouldOverrideExisting) {
        updateHotelFormDataFromPreDefinedPlace(updateFormValues, preDefinedPlace, isUpdateCustomUrlLocally);
        if (updateFormValues.photoUrl) {
          setAutoGeneratedPhoto?.(updateFormValues.photoUrl);
        }
        updateFormAfterFetchHotelDetails(updateFormValues);
        return;
      }
    }

    // Find matching hotel by new findMatchingAccommodationCatalogProperty API
    findMatchingAccommodationCatalogProperty({ sourceId: location.placeId, sourceType: AccommodationPlaceSourceType.google })
      .then(response => {
        const accommodationCatalogProperty = transformPropertyToInventory(response?.data?.findMatchingAccommodationCatalogProperty);
        if (accommodationCatalogProperty) {
          setShowMessage(true, accommodationCatalogProperty.joyPlaceId, accommodationCatalogProperty);
          updateFormValues.joyPlaceId = accommodationCatalogProperty.joyPlaceId;
          if (accommodationCatalogProperty.photos?.length) {
            updateFormValues.photoUrl = accommodationCatalogProperty.photos[0];
            setAutoGeneratedPhoto?.(accommodationCatalogProperty.photos[0]);
          }
          updateHotelFormDataFromCatalogProperty(updateFormValues, response.data.findMatchingAccommodationCatalogProperty, isUpdateCustomUrlLocally);
          updateFormAfterFetchHotelDetails(updateFormValues);
        } else {
          updateFormAfterFetchHotelDetails(updateFormValues);
          addError('Failed to get data from response', { action: 'findMatchingAccommodationCatalogProperty', context: 'accommodationsEditor', location });
        }
      })
      .catch(error => {
        updateFormAfterFetchHotelDetails(updateFormValues);
        addError(error, { action: 'findMatchingAccommodationCatalogProperty', context: 'accommodationsEditor', location });
      });
  };

  return { handleOnSelectHotel };
};
